import React, { useState, useEffect, useCallback } from 'react';
import { Save, Eye, Calendar, Tag, FileText, Settings } from 'lucide-react';
import BlogEditor from './BlogEditor';
import MediaUploader from './MediaUploader';
import { generateSlug, validateSlug } from '../../utils/slugUtils';
import { validateContent } from '../../utils/contentSanitizer';

const BlogPostForm = ({ 
  post = null, 
  onSave, 
  onCancel, 
  loading = false,
  categories = [],
  className = ""
}) => {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    rawContent: null,
    excerpt: '',
    status: 'draft',
    categories: [],
    tags: [],
    featuredImage: null,
    publishDate: new Date().toISOString().slice(0, 16), // Default to current time
    authorName: 'Dental Expert', // Default author name
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: []
    }
  });

  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('content');
  const [slugEdited, setSlugEdited] = useState(false);

  // Initialize form with post data if editing
  useEffect(() => {
    if (post) {
      // Parse rawContent if it's a JSON string (from Firestore storage)
      let parsedRawContent = null;
      if (post.rawContent) {
        try {
          parsedRawContent = typeof post.rawContent === 'string' 
            ? JSON.parse(post.rawContent) 
            : post.rawContent;
        } catch (error) {
          console.warn('Failed to parse rawContent:', error);
          parsedRawContent = null;
        }
      }

      setFormData({
        title: post.title || '',
        slug: post.slug || '',
        content: post.content || '',
        rawContent: parsedRawContent,
        excerpt: post.excerpt || '',
        status: post.status || 'draft',
        categories: post.categories || [],
        tags: post.tags || [],
        featuredImage: post.featuredImage || null,
        publishDate: post.publishDate ? 
          new Date(post.publishDate).toISOString().slice(0, 16) : '',
        authorName: post.author?.name || 'Dental Expert',
        seo: {
          metaTitle: post.seo?.metaTitle || '',
          metaDescription: post.seo?.metaDescription || '',
          keywords: post.seo?.keywords || []
        }
      });
      setSlugEdited(!!post.slug);
    }
  }, [post]);

  // Auto-generate slug when title changes (unless manually edited)
  useEffect(() => {
    if (formData.title && !slugEdited) {
      const autoSlug = generateSlug(formData.title);
      setFormData(prev => ({ ...prev, slug: autoSlug }));
    }
  }, [formData.title, slugEdited]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific error - but don't make the callback depend on errors
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
  }, []); // Remove errors dependency

  // Memoized content change handlers to prevent unnecessary re-renders
  const handleContentChange = useCallback((content) => {
    handleInputChange('content', content);
  }, [handleInputChange]);

  const handleRawContentChange = useCallback((rawContent) => {
    handleInputChange('rawContent', rawContent);
  }, [handleInputChange]);

  const handleSlugChange = (value) => {
    setSlugEdited(true);
    handleInputChange('slug', value);
  };

  const handleCategoriesChange = (selectedCategories) => {
    handleInputChange('categories', selectedCategories);
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    handleInputChange('tags', tags);
  };

  const handleSEOChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      seo: { ...prev.seo, [field]: value }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title cannot exceed 200 characters';
    }

    // Slug validation
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else {
      const slugValidation = validateSlug(formData.slug);
      if (!slugValidation.isValid) {
        newErrors.slug = slugValidation.error;
      }
    }

    // Content validation
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else {
      const contentValidation = validateContent(formData.content);
      if (!contentValidation.isValid) {
        newErrors.content = contentValidation.errors.join(', ');
      }
    }

    // Scheduled post validation
    if (formData.status === 'scheduled') {
      if (!formData.publishDate) {
        newErrors.publishDate = 'Publish date is required for scheduled posts';
      } else {
        const publishTime = new Date(formData.publishDate);
        if (publishTime <= new Date()) {
          newErrors.publishDate = 'Publish date must be in the future';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving post:', error);
      setErrors({ submit: error.message || 'Failed to save post' });
    }
  };

  const tabs = [
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'media', label: 'Featured Image', icon: Eye },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'seo', label: 'SEO', icon: Tag }
  ];

  return (
    <div className={`blog-post-form ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            {post ? 'Edit Post' : 'Create New Post'}
          </h1>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center"
            >
              <Save size={16} className="mr-2" />
              {loading ? 'Saving...' : 'Save Post'}
            </button>
          </div>
        </div>

        {/* Title and Slug */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter post title"
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL Slug *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="url-friendly-slug"
            />
            {errors.slug && (
              <p className="text-red-500 text-sm mt-1">{errors.slug}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              URL: /blog/{formData.slug || 'your-slug-here'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={16} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'content' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <BlogEditor
                  content={formData.content}
                  onChange={handleContentChange}
                  onRawChange={handleRawContentChange}
                  placeholder="Write your blog post content..."
                />
                {errors.content && (
                  <p className="text-red-500 text-sm mt-2">{errors.content}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excerpt
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Brief summary of the post (leave blank for auto-generation)"
                />
                <p className="text-gray-500 text-sm mt-1">
                  {formData.excerpt.length}/200 characters
                </p>
              </div>
            </div>
          )}

          {activeTab === 'media' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Featured Image
              </label>
              <MediaUploader
                currentImage={formData.featuredImage}
                onUpload={(imageUrls) => handleInputChange('featuredImage', imageUrls)}
                onRemove={() => handleInputChange('featuredImage', null)}
              />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author Name
                </label>
                <input
                  type="text"
                  value={formData.authorName}
                  onChange={(e) => handleInputChange('authorName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter author name or initials"
                />
                <p className="text-gray-500 text-sm mt-1">
                  This will be displayed as the author of the blog post
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Publish Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={formData.publishDate}
                  onChange={(e) => handleInputChange('publishDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Set when this post should go live. Posts with future dates will be hidden from public view until that time.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="scheduled">Scheduled</option>
                </select>
              </div>

              {formData.status === 'scheduled' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.publishDate}
                    onChange={(e) => handleInputChange('publishDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  {errors.publishDate && (
                    <p className="text-red-500 text-sm mt-1">{errors.publishDate}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categories
                </label>
                <div className="space-y-2">
                  {categories.map(category => (
                    <label key={category.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.categories.includes(category.name)}
                        onChange={(e) => {
                          const updatedCategories = e.target.checked
                            ? [...formData.categories, category.name]
                            : formData.categories.filter(cat => cat !== category.name);
                          handleCategoriesChange(updatedCategories);
                        }}
                        className="mr-2"
                      />
                      {category.name}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  value={formData.tags.join(', ')}
                  onChange={handleTagsChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="tag1, tag2, tag3"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Separate tags with commas
                </p>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title
                </label>
                <input
                  type="text"
                  value={formData.seo.metaTitle}
                  onChange={(e) => handleSEOChange('metaTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="SEO-optimized title"
                />
                <p className="text-gray-500 text-sm mt-1">
                  {formData.seo.metaTitle.length}/60 characters (optimal: 50-60)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <textarea
                  value={formData.seo.metaDescription}
                  onChange={(e) => handleSEOChange('metaDescription', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Brief description for search engines"
                />
                <p className="text-gray-500 text-sm mt-1">
                  {formData.seo.metaDescription.length}/160 characters (optimal: 150-160)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Keywords
                </label>
                <input
                  type="text"
                  value={formData.seo.keywords.join(', ')}
                  onChange={(e) => {
                    const keywords = e.target.value.split(',').map(kw => kw.trim()).filter(kw => kw.length > 0);
                    handleSEOChange('keywords', keywords);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="keyword1, keyword2, keyword3"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Focus on 3-5 relevant keywords, separated by commas
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Global Error */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{errors.submit}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default BlogPostForm;
